import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';

export default function VVS() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');

  const content = {
    da: {
      metaTitle: 'VVS i nybyggeri – Komplette VVS-løsninger | Billund VVS',
      metaDesc:
        'VVS i alle former for nybyggeri i Billund og omegn. Kvalitet, erfaring og komplette løsninger fra autoriseret VVS-installatør siden 1963. Få et uforpligtende tilbud.',
      canonical: 'https://billundvvs.netlify.app/vvs/',
      badge: 'Autoriseret VVS-installatør siden 1963 • TEKNIQ Garanti',
      h1: 'VVS i alle former for nybyggeri',
      h2: 'Kvalitet, erfaring og komplette løsninger',
      heroLead:
        'Vi udfører VVS-installationer i alle former for nybyggeri – fra enfamiliehuse og rækkehuse til større boligbyggerier, erhvervsbyggeri og offentlige byggerier. Vi sikrer en effektiv proces, høj kvalitet og løsninger, der lever op til gældende krav og standarder.',
      heroCta: 'Få et uforpligtende tilbud',
      heroCall: 'Ring 75 33 12 76',

      // 4-Column Feature Bar matching image
      featureBar: [
        {
          icon: 'fa-solid fa-house',
          title: 'Enfamiliehuse',
          desc: 'Komplette VVS-installationer i nye huse – tilpasset dine behov.',
        },
        {
          icon: 'fa-solid fa-city',
          title: 'Boligbyggeri',
          desc: 'VVS-løsninger til rækkehuse og etageboliger.',
        },
        {
          icon: 'fa-solid fa-industry',
          title: 'Erhvervsbyggeri',
          desc: 'Effektive løsninger til kontor, industri, lager og andre erhvervsbyggerier.',
        },
        {
          icon: 'fa-solid fa-landmark',
          title: 'Offentlige byggerier',
          desc: 'VVS-installationer til skoler, institutioner og andre offentlige bygninger.',
        },
      ],

      // Showcase Grid: "Eksempler på VVS i nybyggeri"
      galleryTitle: 'Eksempler på VVS i nybyggeri',
      gallerySubtitle:
        'Professionelle installationer udført i henhold til bygningsreglementet og højeste håndværksmæssige standarder.',
      galleryItems: [
        {
          title: 'Gulvvarme',
          desc: 'Effektiv og komfortabel varmefordeling',
          image: '/_assets/vvs-gulvvarme-nybyg.jpg',
          alt: 'Gulvvarme i nybyggeri',
        },
        {
          title: 'Sanitet',
          desc: 'Installation af toiletter, badeværelser og køkkener',
          image: '/_assets/vvs-sanitet-indbygning-skelet.jpg',
          alt: 'Sanitet og indbygningscisterner i nybyggeri',
        },
        {
          title: 'Rørinstallationer',
          desc: 'Vand, varme og afløb',
          image: '/_assets/vvs-roer-loft-teknik-nybyg.jpg',
          alt: 'Rørinstallationer og tekniske føringer',
        },
        {
          title: 'Varmesystemer',
          desc: 'Fjernvarme, varmepumper og andre løsninger',
          image: '/_assets/vvs-varmesystemer-manifold-unit.jpg',
          alt: 'Varmesystemer og fordelerrør',
        },
        {
          title: 'Teknikrum',
          desc: 'Komplette VVS-installationer i teknikrum',
          image: '/_assets/vvs-teknikrum-komplet-nybyg.jpg',
          alt: 'Komplet VVS i teknikrum',
        },
        {
          title: 'Afløb',
          desc: 'Kloak og afløb – inde og ude',
          image: '/_assets/vvs-afloeb-kloak-udgravning.jpg',
          alt: 'Kloak og afløb i nybyggeri',
        },
      ],

      // Advice & Value Section
      adviceTitle: 'Tryghed og professionelt håndværk i hverdagen',
      adviceLead:
        'Velfungerende VVS-installationer er fundamentet for et sundt indeklima og en lav energiregning. Vi rådgiver dig ærligt.',
      cards: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Forebyg dyre vandskader i tide',
          text: 'En stor del af VVS-installationerne er skjult i vægge og under gulve. Slidte rør eller begyndende tæring kan forårsage omfattende skader, før det opdages med det blotte øje. Vi tilbyder systematisk eftersyn og trykprøvning, så potentielle problemer udbedres, før skaden sker.',
        },
        {
          icon: 'fa-solid fa-gauge-high',
          title: 'Spar penge med moderne energioptimering',
          text: 'Ældre cirkulationspumper, forkert indregulerede varmeanlæg og slidte armaturer koster unødigt på forbrugsregningen hver eneste måned. Vi gennemgår dit anlæg og viser dig konkrete forbedringer med kort tilbagebetalingstid.',
        },
      ],

      // Tasks Checklist
      tasksTitle: 'VVS-kompetencer fra projektering til aflevering',
      tasks: [
        'Projektering og dimensionering af vand-, varme- og afløbsinstallationer',
        'Komplette gulvvarmesystemer med trådløs eller kablet styring',
        'Montering af indbygningscisterner, armaturer og design-afløb',
        'Opbygning af overskuelige, servicerbare teknikrum og fordelere',
        'Tilslutning til fjernvarme eller montering af energieffektive varmepumper',
        'Mekanisk ventilation med varmegenvinding i tæt nybyggeri',
        'Trykprøvning, tæthedstest og lovpligtig kvalitetssikring',
        'Tæt koordinering med byggeleder, arkitekt og andre fagentreprenører',
      ],

      // Split Dark Section
      darkBannerTitle: 'Fra projektering til færdig installation',
      darkBannerText:
        'Vi rådgiver, projekterer og udfører VVS-installationer i tæt samarbejde med bygherre, arkitekt og øvrige håndværkere. Det sikrer en smidig proces og et godt resultat – hver gang.',
      darkBannerCta: 'Kontakt os i dag',
    },
    en: {
      metaTitle: 'Plumbing for New Construction – Complete Solutions | Billund VVS',
      metaDesc:
        'Plumbing in all types of new buildings in Billund and surrounding areas. Quality, experience and complete turnkey solutions from authorized plumber since 1963.',
      canonical: 'https://billundvvs.netlify.app/en/plumbing/',
      badge: 'Authorised plumber since 1963 • TEKNIQ Guarantee',
      h1: 'Plumbing in all types of new construction',
      h2: 'Quality, experience and complete solutions',
      heroLead:
        'We carry out plumbing installations in all forms of new construction – from detached houses and terraced housing to larger developments, commercial premises and public institutions. We ensure an efficient process, superior craftsmanship and compliance with all building regulations.',
      heroCta: 'Get a non-binding quote',
      heroCall: 'Call +45 75 33 12 76',

      featureBar: [
        {
          icon: 'fa-solid fa-house',
          title: 'Single-family homes',
          desc: 'Complete plumbing installations in new homes – tailored to your needs.',
        },
        {
          icon: 'fa-solid fa-city',
          title: 'Housing developments',
          desc: 'Plumbing solutions for terraced houses and apartment complexes.',
        },
        {
          icon: 'fa-solid fa-industry',
          title: 'Commercial buildings',
          desc: 'Efficient solutions for offices, industrial, warehouse and retail facilities.',
        },
        {
          icon: 'fa-solid fa-landmark',
          title: 'Public sector',
          desc: 'Plumbing installations for schools, care institutions and public buildings.',
        },
      ],

      galleryTitle: 'Examples of plumbing in new construction',
      gallerySubtitle:
        'Professional installations executed strictly according to building codes and the highest trade standards.',
      galleryItems: [
        {
          title: 'Underfloor heating',
          desc: 'Efficient and comfortable heat distribution',
          image: '/_assets/vvs-gulvvarme-nybyg.jpg',
          alt: 'Underfloor heating in new build',
        },
        {
          title: 'Sanitary fixtures',
          desc: 'Installation of toilets, bathrooms and kitchens',
          image: '/_assets/vvs-sanitet-indbygning-skelet.jpg',
          alt: 'Concealed toilet frames and piping',
        },
        {
          title: 'Pipework',
          desc: 'Water, heating and drainage systems',
          image: '/_assets/vvs-roer-loft-teknik-nybyg.jpg',
          alt: 'Piping installations and technical lines',
        },
        {
          title: 'Heating systems',
          desc: 'District heating, heat pumps and custom setups',
          image: '/_assets/vvs-varmesystemer-manifold-unit.jpg',
          alt: 'Heating systems and manifolds',
        },
        {
          title: 'Utility rooms',
          desc: 'Complete plumbing installations in plant rooms',
          image: '/_assets/vvs-teknikrum-komplet-nybyg.jpg',
          alt: 'Complete technical utility room',
        },
        {
          title: 'Drainage',
          desc: 'Sewer and drainage – internal and underground',
          image: '/_assets/vvs-afloeb-kloak-udgravning.jpg',
          alt: 'Underground drainage and sewer',
        },
      ],

      adviceTitle: 'Peace of mind and professional craftsmanship',
      adviceLead:
        'Reliable plumbing installations are the foundation for a healthy indoor climate and long-term energy savings.',
      cards: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Prevent costly water damage in time',
          text: 'Much of your pipework is hidden inside cavity walls and under floorboards. Ageing pipes or silent corrosion can lead to extensive damage before signs become visible. We perform systematic pressure testing and inspections so issues are solved before leaks happen.',
        },
        {
          icon: 'fa-solid fa-gauge-high',
          title: 'Save energy with modern system balancing',
          text: 'Inefficient circulation pumps and incorrectly calibrated heating systems silently inflate monthly utility costs. We inspect your system and suggest concrete upgrades with quick payback periods.',
        },
      ],

      tasksTitle: 'Plumbing competencies from design to handover',
      tasks: [
        'Design and dimensioning of water, heating and sewer networks',
        'Complete underfloor heating systems with smart digital thermostats',
        'Mounting concealed cisterns, designer fixtures and floor drains',
        'Clean layout and assembly of plant rooms and manifolds',
        'Connection to district heating or installation of eco heat pumps',
        'Mechanical ventilation with heat recovery for airtight buildings',
        'Pressure testing, leak inspections and certified documentation',
        'Close coordination with general contractors, architects and trades',
      ],

      darkBannerTitle: 'From planning to finished installation',
      darkBannerText:
        'We advise, design and carry out plumbing installations in close cooperation with clients, architects and other tradesmen. This ensures a seamless process and a superior outcome – every time.',
      darkBannerCta: 'Contact us today',
    },
  };

  const c = isEn ? content.en : content.da;

  return (
    <div className="flex flex-col bg-white">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={c.canonical} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* 1. HERO SECTION */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-10 sm:py-16 lg:py-20 min-h-[460px] lg:min-h-[520px] flex items-center">
        {/* Right side cover image with smooth gradient flow */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[52%] h-full pointer-events-none select-none overflow-hidden">
          <img
            src="/_assets/vvs-nybyggeri-hero-byggeplads.jpg"
            alt={isEn ? 'Plumbing in all types of new construction' : 'VVS i alle former for nybyggeri'}
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Gentle edge gradient transition from white text container into image */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />

          {/* Subtle certification pill in bottom area */}
          <div className="absolute bottom-5 right-6 pointer-events-auto bg-white/90 backdrop-blur-xs px-3.5 py-1.5 rounded-full shadow-xs border border-white/60 flex items-center gap-2 text-xs font-semibold text-[#09263f]">
            <i className="fa-solid fa-certificate text-[#d11218]" aria-hidden="true"></i>
            <span>{isEn ? 'TEKNIQ Authorised 2026–2028' : 'TEKNIQ Autoriseret Installatør 2026–2028'}</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-xl lg:max-w-xl">
            {/* Title & Subtitle */}
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#0a2540] leading-tight">
              {c.h1}
            </h1>
            <p className="text-xl md:text-2xl font-bold text-[#0a2540] mt-2 leading-snug">
              {c.h2}
            </p>

            {/* Lead Text */}
            <p className="text-base leading-relaxed text-[#334155] mt-4">
              {c.heroLead}
            </p>

            {/* CTA Buttons */}
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#d11218] hover:bg-[#b30f14] text-white font-bold text-base rounded-[6px] transition-colors duration-150 group shadow-sm"
              >
                <span>{c.heroCta} &rarr;</span>
              </Link>

              <a
                href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2 px-5 py-3.5 bg-[#f4f6f8] hover:bg-[#e5e9ee] text-[#09263f] font-bold text-base rounded-[6px] transition-colors duration-150"
              >
                <i className="fa-solid fa-phone text-sm text-[#d11218]" aria-hidden="true" />
                <span>{c.heroCall}</span>
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-shield-halved text-emerald-600"></i>
                TEKNIQ Garantiordning
              </span>
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-award text-amber-600"></i>
                Autoriseret VVS siden 1963
              </span>
            </div>

            {/* Mobile & Tablet fallback image */}
            <div className="mt-8 lg:hidden">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[8px] bg-[#e5e9ee] shadow-sm border border-[#e5e9ee]">
                <img
                  src="/_assets/vvs-nybyggeri-hero-byggeplads.jpg"
                  alt={isEn ? 'Plumbing in all types of new construction' : 'VVS i alle former for nybyggeri'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. SEGMENT FEATURE BAR (Blueprint B - 4-Column Icon Grid) */}
      <section className="border-y border-[#e2e8f0] bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-[#e2e8f0]">
          {c.featureBar.map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3.5 px-4 py-3 sm:py-2 first:pl-0 last:pr-0"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0a2540] text-white flex items-center justify-center">
                <i className={`${item.icon} text-base`} aria-hidden="true" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900 leading-tight">
                  {item.title}
                </h4>
                <p className="text-xs text-slate-500 mt-1 leading-normal">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SHOWCASE / SERVICES GRID CARDS (Blueprint C) */}
      <section className="py-14 sm:py-18 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a2540] tracking-tight">
              {c.galleryTitle}
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              {c.gallerySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {c.galleryItems.map((item, idx) => (
              <div
                key={idx}
                className="group flex flex-col rounded-md"
              >
                <div className="aspect-[16/10] w-full overflow-hidden rounded-md bg-slate-100 border border-[#e2e8f0]/80">
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="w-full h-full object-cover group-hover:scale-103 transition duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3">
                  <h3 className="text-base font-bold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-normal">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Central quote button below gallery */}
          <div className="mt-10 text-center">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#d11218] hover:bg-[#b30f14] text-white font-bold text-sm rounded-[6px] transition-colors shadow-sm"
            >
              <span>{isEn ? 'Get a non-binding quote' : 'Få et uforpligtende tilbud'} &rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. ADVICE, VALUE & WATER DAMAGE PREVENTION */}
      <section className="border-t border-[#e2e8f0] bg-[#f8fafc] py-14 sm:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a2540] tracking-tight">
              {c.adviceTitle}
            </h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              {c.adviceLead}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {c.cards.map((card, idx) => (
              <div
                key={idx}
                className="rounded-md border border-[#e2e8f0] bg-white p-6 sm:p-7 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="w-11 h-11 rounded-full bg-[#0a2540] text-white flex items-center justify-center mb-4">
                    <i className={`${card.icon} text-base`} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900">
                    {card.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mt-2.5 leading-relaxed">
                    {card.text}
                  </p>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-500">
                    {isEn ? 'Certified execution' : 'Autoriseret udførelse'}
                  </span>
                  <Link
                    to={isEn ? '/en/get-a-quote' : '/tilbud'}
                    className="font-bold text-[#d60812] hover:text-[#b5070f] inline-flex items-center gap-1"
                  >
                    <span>{isEn ? 'Book checkup' : 'Bestil gennemgang'}</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. TYPICAL EVERYDAY TASKS CHECKLIST */}
      <section className="py-14 sm:py-16 bg-white border-t border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-md border border-[#e2e8f0] bg-white p-6 sm:p-8 lg:p-10 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2e8f0] pb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#0a2540] tracking-tight">
                  {c.tasksTitle}
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">
                  {isEn
                    ? 'Small repairs and major replacements handled with upfront pricing'
                    : 'Mindre reparationer og større udskiftninger udføres altid til gennemskuelige priser'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-[#0a2540] text-xs font-semibold">
                  <i className="fa-solid fa-clock-rotate-left text-[#d60812]" aria-hidden="true" />
                  {isEn ? 'Fast response' : 'Hurtig udrykning'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-[#0a2540] text-xs font-semibold">
                  <i className="fa-solid fa-check text-emerald-600" aria-hidden="true" />
                  {isEn ? 'Fixed price available' : 'Fast tilbud'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3.5 mt-6">
              {c.tasks.map((task, idx) => (
                <div key={idx} className="flex items-start gap-2.5">
                  <i
                    className="fa-solid fa-circle-check text-sm text-[#d60812] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-xs sm:text-sm text-slate-700 leading-normal">
                    {task}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. SPLIT DARK SECTION (Fra projektering til færdig installation - opacity tæt på 0 i højre side) */}
      <section
        className="relative text-white py-16 md:py-20 bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(90deg, #06243d 0%, rgba(6, 36, 61, 0.95) 35%, rgba(6, 36, 61, 0.4) 55%, rgba(6, 36, 61, 0) 80%), url('/_assets/vvs-tegning-planlaegning-montor.jpg')`,
          backgroundPosition: 'center center',
        }}
      >
        <div className="w-full max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-12 relative z-10 flex items-center">
          <div className="max-w-[520px]">
            <h2 className="text-[clamp(26px,3vw,34px)] font-[800] leading-[1.2] m-0 mb-3 text-white">
              {c.darkBannerTitle}
            </h2>
            <p className="text-[15px] leading-[1.6] m-0 mb-6 text-slate-200">
              {c.darkBannerText}
            </p>
            <div className="flex flex-wrap items-center gap-3.5">
              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="inline-flex items-center justify-center gap-4 bg-[#df0011] hover:bg-[#b7000d] text-white px-8 py-3.5 rounded-lg font-[700] text-[16px] transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.99]"
              >
                <span>{c.darkBannerCta}</span>
                <span className="text-[19px] leading-none">→</span>
              </Link>
              <a
                href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 text-white font-[700] text-[15px] border border-white/20 hover:bg-white/20 active:scale-[0.99] transition-all duration-150"
              >
                <i className="fa-solid fa-phone text-[#df0011]" aria-hidden="true" />
                <span>{companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
